import { createServerFn } from "@tanstack/react-start";
import { createClient } from "@supabase/supabase-js";
import type { Database } from "@/integrations/supabase/types";
import demoCharizard from "@/assets/demo-charizard.webp.asset.json";

export type HeroExampleOrder = {
  source: "demo" | "live";
  id: string;
  tcg: string;
  cardName: string;
  setName: string;
  cardNumber: string;
  targetPriceCents: number;
  imageUrl: string | null;
};

const DEMO_ORDER: HeroExampleOrder = {
  source: "demo",
  id: "demo_pkm",
  tcg: "Pokémon",
  cardName: "Charizard PSA 9",
  setName: "Base Set",
  cardNumber: "4/102",
  targetPriceCents: 420000,
  imageUrl: demoCharizard.url,
};

// Trending Right Now uses the 6 most recent open orders; exclude those here.
const TRENDING_EXCLUDE = 6;
const POOL_SIZE = 5;

function mapTcg(s: string | null): string {
  if (!s) return "Pokémon";
  const norm = s.toLowerCase().trim().replace(/[\s-]+/g, "_");
  const map: Record<string, string> = {
    pokemon: "Pokémon",
    pokémon: "Pokémon",
    one_piece: "One Piece",
    onepiece: "One Piece",
    magic_the_gathering: "Magic: The Gathering",
    magic: "Magic: The Gathering",
    mtg: "Magic: The Gathering",
    yugioh: "Yu-Gi-Oh",
    yu_gi_oh: "Yu-Gi-Oh",
    lorcana: "Lorcana",
    digimon: "Digimon",
  };
  return map[norm] ?? s;
}

/**
 * Returns up to 5 open buy orders gaining traction (not already in the top
 * `TRENDING_EXCLUDE` most-recent shown in Trending Right Now). Falls back to
 * the pinned Charizard demo when the pool is empty so the hero is never blank.
 */
export const getHeroExamplePool = createServerFn({ method: "GET" }).handler(
  async (): Promise<HeroExampleOrder[]> => {
    try {
      const supa = createClient<Database>(
        process.env.SUPABASE_URL!,
        process.env.SUPABASE_PUBLISHABLE_KEY!,
        { auth: { storage: undefined, persistSession: false, autoRefreshToken: false } },
      );
      const { data: rows } = await supa
        .from("buy_orders")
        .select("id, tcg, card_name, max_price_cents, created_at, reference_image_path")
        .eq("status", "open")
        .order("created_at", { ascending: false })
        .limit(TRENDING_EXCLUDE + 30);

      const candidates = (rows ?? []).slice(TRENDING_EXCLUDE);
      if (candidates.length === 0) return [DEMO_ORDER];

      // Rank by offer count (traction) then recency.
      let ranked = candidates.slice(0, POOL_SIZE);
      try {
        const ids = candidates.map((r) => r.id);
        const { data: offerRows } = await supa
          .from("offers")
          .select("buy_order_id")
          .in("buy_order_id", ids);
        const counts = new Map<string, number>();
        for (const o of offerRows ?? []) {
          counts.set(o.buy_order_id, (counts.get(o.buy_order_id) ?? 0) + 1);
        }
        ranked = candidates
          .map((r) => ({ r, c: counts.get(r.id) ?? 0 }))
          .sort((a, b) => b.c - a.c)
          .slice(0, POOL_SIZE)
          .map((x) => x.r);
      } catch {
        // ignore — fall back to recency-ordered slice above
      }

      // Sign reference images in batch.
      const urlByPath = new Map<string, string>();
      const paths = ranked.map((r) => r.reference_image_path).filter((p): p is string => !!p);
      if (paths.length > 0) {
        try {
          const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
          const { data: signed } = await supabaseAdmin.storage
            .from("buy-order-images")
            .createSignedUrls(paths, 60 * 60);
          for (const s of signed ?? []) {
            if (s.path && s.signedUrl) urlByPath.set(s.path, s.signedUrl);
          }
        } catch {
          // ignore — images will simply be omitted
        }
      }

      const pool: HeroExampleOrder[] = ranked.map((r) => ({
        source: "live",
        id: r.id,
        tcg: mapTcg(r.tcg),
        cardName: r.card_name ?? "Untitled",
        setName: "",
        cardNumber: "",
        targetPriceCents: r.max_price_cents ?? 0,
        imageUrl: r.reference_image_path ? urlByPath.get(r.reference_image_path) ?? null : null,
      }));
      return pool.length > 0 ? pool : [DEMO_ORDER];
    } catch (e) {
      console.error("[hero-example] pool failed", e);
      return [DEMO_ORDER];
    }
  },
);
